import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CaretDown,
  CaretUp,
  FunnelSimple,
  MagnifyingGlass,
  X
} from '@phosphor-icons/react';

const TaskTypeMultiSelectDropdown = ({
  items,
  selectedValues,
  onChange
}) => {
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllChips, setShowAllChips] = useState(false);

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
  const selectedItems = useMemo(
    () => items.filter((item) => selectedSet.has(item.value)),
    [items, selectedSet]
  );
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const visibleItems = useMemo(() => items.filter((item) => (
    !normalizedSearch
    || `${item.label} ${item.searchText || ''}`.toLowerCase().includes(normalizedSearch)
  )), [items, normalizedSearch]);
  const hasManySelected = selectedItems.length > 3;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.requestAnimationFrame(() => searchInputRef.current?.focus());

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!hasManySelected) {
      setShowAllChips(false);
    }
  }, [hasManySelected]);

  const toggleValue = (value) => {
    onChange(
      selectedSet.has(value)
        ? selectedValues.filter((selectedValue) => selectedValue !== value)
        : [...selectedValues, value]
    );
  };

  const buttonLabel = selectedValues.length
    ? `${selectedValues.length} ${selectedValues.length === 1 ? 'tipo seleccionado' : 'tipos seleccionados'}`
    : 'Todos los tipos de tarea';

  return (
    <div ref={containerRef} className="relative">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm tracking-[0.04em] text-content">Tipos de tarea</div>
          <div className="mt-0.5 text-xs text-content-muted">Sin selección equivale a todos</div>
        </div>
        <span className="shrink-0 rounded-full border border-line bg-surface px-2.5 py-1 text-[11px] font-medium text-content-muted">
          {selectedValues.length} sel.
        </span>
      </div>

      <button
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onClick={() => setIsOpen((current) => !current)}
        className={`flex w-full items-center gap-3 rounded-xl border bg-surface-2 px-4 py-3 text-left transition ${
          isOpen
            ? 'border-line ring-1 ring-amber'
            : 'border-line hover:border-line'
        }`}
      >
        <FunnelSimple size={20} className="shrink-0 text-content-muted" />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-content">{buttonLabel}</span>
        {isOpen
          ? <CaretUp size={15} className="shrink-0 text-content-muted" />
          : <CaretDown size={15} className="shrink-0 text-content-muted" />}
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-line bg-surface-2 shadow-[0_24px_70px_rgba(0,0,0,0.55)]">
          <label className="relative block border-b border-line p-2.5">
            <MagnifyingGlass
              size={18}
              className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-content-muted"
            />
            <input
              ref={searchInputRef}
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Buscar tipo de tarea..."
              className="w-full rounded-lg border border-transparent bg-surface py-2.5 pl-10 pr-3 text-sm text-content outline-none transition placeholder:text-content-muted focus:border-amber"
            />
          </label>

          <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
            <button
              type="button"
              onClick={() => onChange(items.map((item) => item.value))}
              className="text-xs font-semibold text-content transition hover:text-accent-text"
            >
              Seleccionar todos
            </button>
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-xs font-medium text-content-muted transition hover:text-rose-300"
            >
              Limpiar
            </button>
          </div>

          <div role="listbox" aria-multiselectable="true" className="max-h-72 overflow-y-auto p-2">
            {!visibleItems.length && (
              <div className="rounded-lg border border-dashed border-line px-3 py-5 text-center text-sm text-content-muted">
                No se encontraron tipos de tarea.
              </div>
            )}

            {visibleItems.map((item) => {
              const isSelected = selectedSet.has(item.value);
              return (
                <label
                  key={item.value}
                  role="option"
                  aria-selected={isSelected}
                  className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2.5 transition hover:bg-surface"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleValue(item.value)}
                    className="h-4 w-4 rounded border-line bg-surface text-accent-text"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-content">{item.label}</span>
                  <span className="shrink-0 text-xs text-content-muted">{item.taskCount || 0}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {selectedItems.length > 0 && (
        <div className={`relative mt-3 ${!showAllChips && hasManySelected ? 'pr-20' : ''}`}>
          <div className={`flex flex-wrap gap-1.5 ${!showAllChips && hasManySelected ? 'max-h-7 overflow-hidden' : ''}`}>
            {selectedItems.map((item) => (
              <span
                key={item.value}
                className="inline-flex h-7 max-w-full items-center gap-1 rounded-full border border-line bg-surface-2 px-2.5 text-[11px] font-medium text-content"
              >
                <span className="truncate">{item.label}</span>
                <button
                  type="button"
                  aria-label={`Quitar ${item.label}`}
                  onClick={() => toggleValue(item.value)}
                  className="shrink-0 rounded-full text-content-muted transition hover:text-content"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>

          {hasManySelected && (
            <button
              type="button"
              onClick={() => setShowAllChips((current) => !current)}
              className={`rounded-full border border-line bg-surface px-2.5 py-1 text-[10px] font-semibold text-accent-text transition hover:border-amber hover:text-accent-text ${
                showAllChips ? 'mt-2' : 'absolute right-0 top-0'
              }`}
            >
              {showAllChips ? 'Ver menos' : 'Ver más'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskTypeMultiSelectDropdown;
